import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import axios from 'axios'
import { headerStore } from '@/stores/header'
import { CompletionTwoLevel } from '@/types/v2/completion2/completionTwoLevel'
import { CompletionTwoGroup } from '@/types/v2/completion2/completionTwoGroup'
import { CompletionTwoRoom } from '@/types/v2/completion2/completionTwoRoom'
import { CompletionTwoElementGroup } from '@/types/v2/completion2/completionTwoElementGroup'
import { CompletionTwoElement } from '@/types/v2/completion2/completionTwoElement'
import { subtotalStore } from '@/stores/v2/subtotal'

export const completion2Store = defineStore('v2/completion2', () => {
  const loading = ref({ rooms: false, elementGroups: false, elements: false })
  const saving = computed(() => savingProductId.value > 0)
  const savingProductId = ref(0)
  const phaseState = ref('loading')
  const phaseClosed = computed(() => ['closed', 'processed', 'expired'].includes(phaseState.value) || headerStore().ordersReceived.completion)

  const levelsWithRooms = ref<CompletionTwoLevel[]>([])
  const groupsWithElementGroups = ref<CompletionTwoGroup[]>([])
  const elementsWithProducts = ref<CompletionTwoElement[]>([])
  const productSort = ref('priceAsc')

  const selectedRoomId = ref<number|null>(null)
  const selectedRoom = computed(() =>
    levelsWithRooms.value
      .flatMap((level:CompletionTwoLevel) => level.rooms)
      .find((room:CompletionTwoRoom) => room.id === selectedRoomId.value) || null
  )

  const selectedElementGroupId = ref<number|null>(null)
  const selectedElementGroup = computed<CompletionTwoElementGroup|null>(() =>
    groupsWithElementGroups.value
      .flatMap((group:CompletionTwoGroup) => group.elementGroups)
      .find((elementGroup:CompletionTwoElementGroup) => elementGroup.id === selectedElementGroupId.value) || null
  )

  const selectedElementGroupConfigurationId = ref<number|null>(null)

  function getRooms () : void {
    loading.value.rooms = true
    axios.get(process.env.VUE_APP_API_URL_V2 + '/completion2/rooms').then((r) => {
      if (r.data.length) {
        levelsWithRooms.value = r.data
        selectedRoomId.value = r.data[0]?.rooms[0]?.id || null
        getElementGroups()
      }
    }).finally(() => {
      loading.value.rooms = false
    })
  }

  function getElementGroups () : Promise<void> {
    loading.value.elementGroups = true
    return axios.get(process.env.VUE_APP_API_URL_V2 + '/completion2/rooms/' + selectedRoomId.value + '/elementGroups').then((r) => {
      groupsWithElementGroups.value = r.data
      selectedElementGroupId.value = r.data[0]?.elementGroups[0]?.id || null
      if (groupsWithElementGroups.value.length) {
        getProducts()
      }
    }).finally(() => {
      loading.value.elementGroups = false
    })
  }

  function getProducts () : void {
    loading.value.elements = true
    axios.get(process.env.VUE_APP_API_URL_V2 + '/completion2/rooms/' + selectedRoomId.value + '/elementGroups/' + selectedElementGroupId.value + '/elements?sort=' + productSort.value).then((r) => {
      elementsWithProducts.value = r.data
      selectedElementGroupConfigurationId.value = r.data[0].element_group_configuration_id || null
    }).finally(() => {
      loading.value.elements = false
    })
  }

  function selectProduct (productId: number): Promise<void> {
    savingProductId.value = productId

    return axios.post(
      process.env.VUE_APP_API_URL_V2 + '/completion2/rooms/' + selectedRoomId.value + '/elementGroups/' + selectedElementGroupId.value + '/elementGroupConfiguration/' + selectedElementGroupConfigurationId.value + '/product/' + productId + '?sort=' + productSort.value
    ).then((r) => {
      elementsWithProducts.value = r.data
      subtotalStore().getSubtotals()
    }).finally(() => {
      savingProductId.value = 0
    })
  }

  function getPhaseState () : void {
    axios.get(process.env.VUE_APP_API_URL + '/phase/2/closed').then((r) => {
      phaseState.value = r.data.status
    })
  }

  return {
    loading,
    saving,
    savingProductId,
    phaseState,
    phaseClosed,
    levelsWithRooms,
    groupsWithElementGroups,
    elementsWithProducts,
    productSort,
    selectedRoomId,
    selectedRoom,
    selectedElementGroupId,
    selectedElementGroup,
    selectedElementGroupConfigurationId,
    getRooms,
    getElementGroups,
    getProducts,
    selectProduct,
    getPhaseState
  }
})
